import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { 
  Search, Filter, Mail, Upload, Send, CheckCircle, Clock, Palette,
  MessageCircle, Eye, Paintbrush, Plus
} from "lucide-react";

const Invitaciones = () => {
  const [invitaciones, setInvitaciones] = useState([]);
  const [clientes, setClientes] = useState({});
  const [clientesList, setClientesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstatus, setFilterEstatus] = useState("todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvitacion, setSelectedInvitacion] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({ archivo_url: "", notas_diseno: "" });
  const [newInvDialog, setNewInvDialog] = useState(false);
  const [newInvClienteId, setNewInvClienteId] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [invitacionesRes, clientesRes] = await Promise.all([
        axios.get(`${API}/invitaciones`),
        axios.get(`${API}/clientes`)
      ]);
      setInvitaciones(invitacionesRes.data);
      const clientesMap = {};
      clientesRes.data.forEach(c => { clientesMap[c.cliente_id] = c; });
      setClientes(clientesMap);
      setClientesList(clientesRes.data);
    } catch {
      toast.error("Error al cargar las invitaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdateEstatus = async (invitacionId, nuevoEstatus) => {
    try {
      await axios.put(`${API}/invitaciones/${invitacionId}`, { estatus: nuevoEstatus });
      toast.success("Estado actualizado");
      await fetchData();
    } catch (error) {
      toast.error("Error al actualizar el estado");
    }
  };

  const handleUploadInvitacion = async () => {
    if (!selectedInvitacion) return;
    try {
      await axios.put(`${API}/invitaciones/${selectedInvitacion.invitacion_id}`, {
        archivo_url: uploadData.archivo_url,
        notas_diseno: uploadData.notas_diseno,
        estatus: "completada"
      });
      toast.success("Invitación actualizada");
      setUploadData({ archivo_url: "", notas_diseno: "" });
      await fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al actualizar la invitación");
    }
  };

  const handleMarcarEntregada = async (invitacionId) => {
    try {
      await axios.put(`${API}/invitaciones/${invitacionId}`, { estatus: "entregada" });
      toast.success("Invitación marcada como entregada");
      await fetchData();
    } catch (error) {
      toast.error("Error al marcar como entregada");
    }
  };

  const openUploadDialog = (invitacion) => {
    setSelectedInvitacion(invitacion);
    setUploadData({ archivo_url: invitacion.archivo_url || "", notas_diseno: invitacion.notas_diseno || "" });
    setDialogOpen(true);
  };

  const handleCrearInvitacion = async () => {
    if (!newInvClienteId) {
      toast.error("Selecciona un cliente");
      return;
    }
    try {
      await axios.post(`${API}/invitaciones`, { cliente_id: newInvClienteId });
      toast.success("Invitación creada");
      setNewInvClienteId("");
      setNewInvDialog(false);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear la invitación");
    }
  };

  const filteredInvitaciones = invitaciones.filter(inv => {
    const cliente = clientes[inv.cliente_id];
    const matchesSearch = cliente?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cliente?.nombre_festejado?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterEstatus === "todos" || inv.estatus === filterEstatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      pendiente: { label: "Pendiente", className: "badge-pending", icon: Clock },
      en_diseno: { label: "En Diseño", className: "badge-design", icon: Palette },
      completada: { label: "Completada", className: "badge-validated", icon: CheckCircle },
      entregada: { label: "Entregada", className: "badge-delivered", icon: Send },
    };
    return badges[status] || { label: status, className: "badge", icon: Clock };
  };

  const pendientes = invitaciones.filter(i => i.estatus === "pendiente").length;
  const enDiseno = invitaciones.filter(i => i.estatus === "en_diseno").length;
  const completadas = invitaciones.filter(i => i.estatus === "completada").length;
  const entregadas = invitaciones.filter(i => i.estatus === "entregada").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Invitaciones</h1>
          <p className="text-stone-600 mt-1">Diseño y entrega de invitaciones digitales</p>
        </div>
        <Button onClick={() => setNewInvDialog(true)} className="btn-primary" data-testid="btn-nueva-invitacion">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Invitación
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Pendientes", value: pendientes, color: "amber" },
          { label: "En Diseño", value: enDiseno, color: "blue" },
          { label: "Completadas", value: completadas, color: "emerald" },
          { label: "Entregadas", value: entregadas, color: "stone" },
        ].map((stat) => (
          <Card key={stat.label} className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase tracking-wider">{stat.label}</p>
              <p className="font-display text-2xl text-stone-900">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input placeholder="Buscar por cliente o festejado..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-stone-300" data-testid="search-invitaciones" />
        </div>
        <Select value={filterEstatus} onValueChange={setFilterEstatus}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-invitaciones">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="en_diseno">En Diseño</SelectItem>
            <SelectItem value="completada">Completadas</SelectItem>
            <SelectItem value="entregada">Entregadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredInvitaciones.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvitaciones.map((inv) => {
            const cliente = clientes[inv.cliente_id];
            const badge = getStatusBadge(inv.estatus);
            return (
              <Card key={inv.invitacion_id} className="border-stone-200 hover:shadow-lg transition-shadow" data-testid={`inv-card-${inv.invitacion_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-medium text-stone-900">{cliente?.nombre || "N/A"}</p>
                      <p className="text-sm text-stone-500">{cliente?.tipo_evento}</p>
                    </div>
                    <span className={`badge ${badge.className}`}>{badge.label}</span>
                  </div>
                  
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-stone-500">Festejado:</span>
                      <span className="text-stone-700">{cliente?.nombre_festejado}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Fecha:</span>
                      <span className="text-stone-700">{cliente?.fecha_evento}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Tema:</span>
                      <span className="text-stone-700">{cliente?.tema || "No especificado"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Colores:</span>
                      <span className="text-stone-700">{cliente?.colorimetria || "No especificado"}</span>
                    </div>
                  </div>

                  {inv.archivo_url && (
                    <div className="mb-4 p-3 bg-stone-50 rounded-sm">
                      <a href={inv.archivo_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-900 hover:underline text-sm">
                        <Eye className="w-4 h-4" />
                        Ver invitación
                      </a>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    {/* DESIGNER BUTTON - Always visible */}
                    <Link to={`/panel/invitaciones/${inv.invitacion_id}/disenar`}>
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs" data-testid={`btn-disenar-${inv.invitacion_id}`}>
                        <Paintbrush className="w-3 h-3 mr-1" />
                        Diseñar
                      </Button>
                    </Link>

                    {inv.estatus === "pendiente" && (
                      <Button size="sm" onClick={() => handleUpdateEstatus(inv.invitacion_id, "en_diseno")} className="btn-primary text-xs" data-testid={`btn-iniciar-${inv.invitacion_id}`}>
                        <Palette className="w-3 h-3 mr-1" />
                        Iniciar Diseño
                      </Button>
                    )}
                    
                    {inv.estatus === "en_diseno" && (
                      <Button size="sm" onClick={() => openUploadDialog(inv)} className="btn-primary text-xs" data-testid={`btn-subir-${inv.invitacion_id}`}>
                        <Upload className="w-3 h-3 mr-1" />
                        Subir Diseño
                      </Button>
                    )}
                    
                    {inv.estatus === "completada" && (
                      <Button size="sm" onClick={() => handleMarcarEntregada(inv.invitacion_id)} className="btn-accent text-xs" data-testid={`btn-entregar-${inv.invitacion_id}`}>
                        <Send className="w-3 h-3 mr-1" />
                        Entregar
                      </Button>
                    )}
                    
                    {cliente && (
                      <a href={`https://wa.me/52${cliente.whatsapp}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-stone-300 text-xs">
                          <MessageCircle className="w-3 h-3 mr-1" />
                          WhatsApp
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="font-display text-xl text-stone-900 mb-2">No se encontraron invitaciones</h3>
          <p className="text-stone-600">Los clientes deben registrarse primero para generar invitaciones</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Subir Invitación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>URL del archivo (imagen o PDF)</Label>
              <Input value={uploadData.archivo_url} onChange={(e) => setUploadData(prev => ({ ...prev, archivo_url: e.target.value }))} placeholder="https://..." className="border-stone-300" data-testid="input-archivo-url" />
              <p className="text-xs text-stone-500">Sube el archivo a un servicio de almacenamiento y pega el enlace aquí</p>
            </div>
            <div className="space-y-2">
              <Label>Notas del diseño</Label>
              <Textarea value={uploadData.notas_diseno} onChange={(e) => setUploadData(prev => ({ ...prev, notas_diseno: e.target.value }))} placeholder="Detalles sobre el diseño..." rows={3} className="border-stone-300" data-testid="textarea-notas-diseno" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleUploadInvitacion} className="btn-primary" data-testid="btn-guardar-invitacion">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nueva Invitación Dialog */}
      <Dialog open={newInvDialog} onOpenChange={setNewInvDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nueva Invitación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Selecciona un cliente</Label>
              <Select value={newInvClienteId} onValueChange={setNewInvClienteId}>
                <SelectTrigger className="border-stone-300" data-testid="select-cliente-inv">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientesList.map(cl => (
                    <SelectItem key={cl.cliente_id} value={cl.cliente_id}>
                      {cl.nombre} - {cl.tipo_evento} ({cl.nombre_festejado})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewInvDialog(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleCrearInvitacion} className="btn-primary" data-testid="btn-crear-invitacion">
              Crear Invitación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invitaciones;
